import { LitElement, html, css } from 'lit';
import { customElement } from 'lit/decorators.js';
import '../components/site-footer.js';
import { applyDefaultDocumentSeo } from '../lib/seo';
import { contactHref } from '../lib/router-path';
import {
  BOOKING_URL,
  CONTACT_EMAIL,
  CONTACT_PHONE_LABEL,
  contactTelUrl,
  contactWhatsAppUrl,
} from '../lib/site-contact';

@customElement('app-home')
export class AppHome extends LitElement {
  connectedCallback(): void {
    super.connectedCallback();
    applyDefaultDocumentSeo();
  }

  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
      background: var(--bg);
      color: var(--text);
      font-family: Inter, system-ui, -apple-system, sans-serif;
      overflow-x: hidden;
    }

    .visually-hidden {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    .wrap {
      box-sizing: border-box;
      width: 100%;
      max-width: var(--layout-max-width, 1120px);
      margin: 0 auto;
      padding-top: 0;
      padding-bottom: 2rem;
      padding-left: max(var(--layout-padding-inline, 1.25rem), env(safe-area-inset-left, 0px));
      padding-right: max(var(--layout-padding-inline, 1.25rem), env(safe-area-inset-right, 0px));
    }

    /* --- Hero: copy + photo — same row height; image fills copy column height --- */
    .hero-split {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(240px, 44%);
      gap: clamp(1.5rem, 4vw, 3rem);
      align-items: stretch;
      padding: 2.5rem 0 3.25rem;
      margin-bottom: 0;
    }

    .hero-copy {
      text-align: left;
      display: flex;
      flex-direction: column;
    }

    .eyebrow {
      font-size: 0.8125rem;
      font-weight: 600;
      letter-spacing: 0.09em;
      text-transform: uppercase;
      color: var(--text-muted);
      margin-bottom: 0.5rem;
    }

    .hero-copy h1 {
      font-size: clamp(1.85rem, 4.2vw, 3rem);
      line-height: 1.08;
      font-weight: 800;
      margin: 0 0 1rem;
    }

    /* Single hero description: hook + service keywords for humans & SEO */
    .hero-lede {
      font-size: 1.0625rem;
      line-height: 1.58;
      color: var(--text-muted);
      margin: 0 0 1.5rem;
      max-width: 40rem;
    }

    .hero-lede strong {
      color: var(--text);
      font-weight: 600;
    }

    .hero-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
    }

    .hero-visual {
      min-height: 0;
      height: 100%;
      align-self: stretch;
      border-radius: var(--radius-md, 8px);
      background: center / cover no-repeat;
      background-image: linear-gradient(160deg, rgba(0, 0, 0, 0.12) 0%, rgba(0, 0, 0, 0.38) 100%),
        url(/assets/landing/creative-team.jpg);
      border: 1px solid var(--border-card, rgba(65, 65, 65, 0.35));
      box-shadow: var(--shadow, 0 8px 24px rgba(0, 0, 0, 0.2));
    }

    .btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0.75rem 1.25rem;
      border-radius: var(--radius-sm, 4px);
      font-weight: 600;
      font-size: 1rem;
      text-decoration: none;
      border: 1px solid transparent;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
    }

    .btn-primary {
      background-color: var(--brand-accent);
      color: var(--text-dark);
      border-color: var(--brand-accent);
    }

    .btn-primary:hover {
      background-color: var(--brand-accent-deep);
      border-color: var(--brand-accent-deep);
      color: var(--text-dark);
    }

    a.btn-primary:visited {
      color: var(--text-dark);
    }

    .btn-secondary {
      background: var(--bg-soft);
      color: var(--text);
      border-color: rgba(1, 60, 108, 0.4);
    }

    .btn-secondary:hover {
      border-color: var(--primary);
    }

    /* --- Sections: spacing only (no rules between blocks) --- */
    .section-block {
      margin-top: 0;
      padding-top: 3.25rem;
      padding-bottom: 0.25rem;
    }

    .section-heading {
      font-size: clamp(1.35rem, 2.8vw, 1.85rem);
      line-height: 1.15;
      font-weight: 800;
      margin: 0 0 1.1rem;
      color: var(--text);
    }

    /* --- What we do: three image tiles --- */
    .pillars {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 0.75rem;
    }

    .pillar {
      position: relative;
      min-height: 220px;
      padding: 1.15rem;
      border-radius: var(--radius-md, 8px);
      overflow: hidden;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      text-align: left;
      border: 1px solid var(--border-card, rgba(65, 65, 65, 0.35));
      background: center / cover no-repeat;
      isolation: isolate;
    }

    .pillar::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.92) 0%,
        rgba(0, 0, 0, 0.75) 40%,
        rgba(0, 0, 0, 0.35) 68%,
        rgba(0, 0, 0, 0.1) 100%
      );
      z-index: 0;
      border-radius: inherit;
    }

    .pillar h3,
    .pillar p {
      position: relative;
      z-index: 1;
    }

    .pillar h3 {
      font-size: 1.05rem;
      margin: 0 0 0.35rem;
      font-weight: 700;
      color: #fff;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
    }

    .pillar p {
      margin: 0;
      font-size: 0.875rem;
      line-height: 1.45;
      color: rgba(255, 255, 255, 0.95);
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.55);
    }

    .pillar--a {
      background-image: url(/assets/landing/wellness-focus.jpg);
    }

    .pillar--b {
      background-image: url(/assets/landing/hands-craft.jpg);
    }

    .pillar--c {
      background-image: url(/assets/landing/calm-desk.jpg);
    }

    /* --- Talk to us: open split (like hero), photo as its own card --- */
    .contact-section > .section-heading {
      margin-bottom: 1rem;
    }

    .contact-panel {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(240px, 44%);
      gap: clamp(1.35rem, 3.5vw, 2.75rem);
      align-items: center;
      padding: 0.15rem 0 0;
    }

    .contact-copy {
      text-align: left;
      padding: 0.35rem 0 0.35rem 1.2rem;
      border-left: 3px solid var(--brand-secondary);
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .contact-copy > p {
      margin: 0 0 1.05rem;
      color: var(--text-muted);
      line-height: 1.55;
      font-size: 1.05rem;
      max-width: 30rem;
    }

    .contact-copy .hero-actions {
      margin-top: 0;
    }

    .contact-visual {
      min-height: 0;
      width: 100%;
      aspect-ratio: 4 / 3;
      max-height: min(340px, 52vw);
      border-radius: var(--radius-md, 8px);
      border: 1px solid var(--border-card, rgba(65, 65, 65, 0.35));
      box-shadow: var(--shadow, 0 8px 24px rgba(0, 0, 0, 0.2));
      background: center / cover no-repeat;
      background-image: linear-gradient(160deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.4) 100%),
        url(/assets/landing/creative-meeting.jpg);
    }

    @media (max-width: 840px) {
      .hero-split {
        grid-template-columns: 1fr;
      }

      .hero-copy {
        text-align: center;
      }

      .hero-lede {
        margin-left: auto;
        margin-right: auto;
      }

      .hero-actions {
        justify-content: center;
        margin-top: 0;
      }

      .hero-visual {
        display: none;
      }

      .pillars {
        grid-template-columns: 1fr;
      }

      .pillar {
        min-height: 180px;
      }

      .contact-panel {
        grid-template-columns: 1fr;
        gap: 1.35rem;
      }

      .contact-copy {
        text-align: center;
        padding: 1.1rem 0.5rem 0;
        border-left: none;
        border-top: 3px solid var(--brand-secondary);
      }

      .contact-copy > p {
        margin-left: auto;
        margin-right: auto;
      }

      .contact-copy .hero-actions {
        justify-content: center;
      }

      .contact-visual {
        aspect-ratio: 16 / 9;
        max-height: 200px;
        margin: 0 auto;
        width: 100%;
        max-width: 520px;
      }
    }
  `;

  render() {
    const emailBtn = html`<a
      class="btn btn-secondary"
      href="mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Yes We Can LLC — inquiry')}"
      >Email us</a
    >`;
    const whatsAppBtn = html`<a
      class="btn btn-secondary"
      href="${contactWhatsAppUrl()}"
      target="_blank"
      rel="noopener noreferrer"
      >WhatsApp</a
    >`;
    const callBtn = html`<a class="btn btn-secondary" href="${contactTelUrl()}">Call ${CONTACT_PHONE_LABEL}</a>`;

    return html`
      <div class="wrap">
        <header class="hero-split">
          <div class="hero-copy">
            <h1>Need help? Yes we can help.</h1>
            <p class="hero-lede">
              What do you need today? At Yes We Can LLC, we can help with <strong>custom software &amp; web apps</strong>,
              <strong>mobile apps</strong>, <strong>immigration &amp; visas</strong>, <strong>taxes</strong>,
              <strong>divorce</strong>, <strong>SNAP &amp; FDA registration</strong>, <strong>imports &amp; supplier checks</strong>,
              <strong>graphic design &amp; business cards</strong>, <strong>POS</strong>, <strong>insurance</strong>,
              <strong>bankruptcy</strong>, <strong>admissions paperwork</strong>—or a warm intro to someone in our network
              when it’s not our lane.
            </p>
            <div class="hero-actions">
              <a class="btn btn-primary" href="${BOOKING_URL}" target="_blank" rel="noopener noreferrer"
                >Book a free consult</a>
              <a class="btn btn-secondary" href="${contactHref()}">Contact</a>
            </div>
          </div>
          <div class="hero-visual" aria-hidden="true"></div>
        </header>

        <section class="section-block" aria-labelledby="what-heading">
          <h2 class="section-heading" id="what-heading">What we do</h2>
          <div class="pillars">
            <article class="pillar pillar--a">
              <h3>We do it</h3>
              <p>Apps, forms, design—here, with you.</p>
            </article>
            <article class="pillar pillar--b">
              <h3>We find help</h3>
              <p>Not our job? We know who to call. People we trust.</p>
            </article>
            <article class="pillar pillar--c">
              <h3>Ask</h3>
              <p>Anything. Big, small, weird. Start here.</p>
            </article>
          </div>
        </section>

        <section class="section-block contact-section" id="contact" aria-labelledby="contact-heading">
          <h2 class="section-heading" id="contact-heading">Talk to us</h2>
          <div class="contact-panel">
            <div class="contact-copy">
              <p>
                Choose any channel below, or open our <a href="${contactHref()}">Contact</a> page for all options.
              </p>
              <div class="hero-actions">
                <a class="btn btn-primary" href="${BOOKING_URL}" target="_blank" rel="noopener noreferrer">Calendar</a>
                ${emailBtn} ${whatsAppBtn} ${callBtn}
                <a class="btn btn-secondary" href="${contactHref()}">Contact page</a>
              </div>
            </div>
            <div class="contact-visual" aria-hidden="true"></div>
          </div>
        </section>
      </div>
      <site-footer></site-footer>
    `;
  }
}
